import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { useMutation } from "@apollo/react-hooks";
import { InputAdornment, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useStyles from "./new-message.style";
import SendIcon from "@iconify/icons-mdi/send";
import { InlineIcon } from "@iconify/react";
import {
  MY_LEAVE_CREATE_MESSAGE,
  MY_LEAVE_GET_REQUEST,
} from "my-leave/graphql";
import { useOcClientState } from "oc/hooks";

interface MyLeaveNewMessageProps {
  loading: boolean;
  selectedRequest: string;
  selectedEmployeeId: string;
  disabled: boolean;
  role: string;
}

export const MyLeaveNewMessage: React.FC<MyLeaveNewMessageProps> = ({
  loading,
  selectedRequest,
  selectedEmployeeId,
  disabled,
  role,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [newMessageText, setNewMessageText] = useOcClientState<string>(
    "newMessageText",
    "",
  );

  const [newMessageError, setNewMessageError] = useOcClientState<boolean>(
    "newMessageError",
    false,
  );

  const [createLeaveMessage] = useMutation(MY_LEAVE_CREATE_MESSAGE);

  const handleNewMessage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessageText(e.target.value);
  };

  const handleSubmitNewMessage = () => {
    if (newMessageText === "") {
      setNewMessageError(true);
      return;
    }
    let variables = {
      request: selectedRequest,
      message: newMessageText,
      locale: i18n.language,
      role: role,
    };
    createLeaveMessage({
      variables: variables,
      optimisticResponse: {
        __typename: "Mutation",
        createLeaveMessage: {
          __typename: "LeaveMessage",
          createdAt: new Date().toUTCString(),
          createdBy: selectedEmployeeId,
          message: newMessageText,
          employeeAvatar: "",
          employeeName: "temporary",
        },
      },
      update: (proxy, { data: { createLeaveMessage } }) => {
        const data = proxy.readQuery({
          query: MY_LEAVE_GET_REQUEST,
          variables: { _id: selectedRequest },
        }) as { leaveRequest: { messages: any[] } };
        let newData = _.cloneDeep(data);

        newData.leaveRequest.messages = [
          ...newData.leaveRequest.messages,
          createLeaveMessage,
        ];

        proxy.writeQuery({
          query: MY_LEAVE_GET_REQUEST,
          data: newData,
        });
      },
    });

    setNewMessageText("");
  };

  useEffect(() => {
    return () => {
      setNewMessageText("");
      setNewMessageError(false);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <TextField
      id="new-message-box-input"
      multiline={newMessageText?.length > 50 ? true : false}
      fullWidth
      variant="outlined"
      placeholder={t("OC_WRITE_MESSAGE")}
      value={newMessageText}
      onChange={handleNewMessage}
      onFocus={() => setNewMessageError(false)}
      error={newMessageError}
      helperText={
        newMessageError
          ? `${t("OC_EMPTY_MESSAGE")}`
          : `${newMessageText.length}/255`
      }
      style={{
        height: "auto",
      }}
      disabled={disabled}
      inputProps={{ maxLength: 255 }}
      FormHelperTextProps={
        newMessageError
          ? { style: { textAlign: "left" } }
          : { style: { textAlign: "right" } }
      }
      InputProps={{
        disabled: loading || disabled,
        style: {
          backgroundColor: "white",
        },
        endAdornment: (
          <InputAdornment position="end">
            <InlineIcon
              id="my-leave-new-message-button"
              icon={SendIcon}
              className={classes.iconSend}
              onClick={handleSubmitNewMessage}
              style={{
                cursor: "pointer",
                backgroundColor: theme.palette.gray20.main,
                padding: "13px",
                borderRadius: "25px",
              }}
            />
          </InputAdornment>
        ),
      }}
    />
  );
};

export default MyLeaveNewMessage;
